import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout';
import { useI18n } from '../../contexts/I18nContext';

export default function Terms() {
  const { legal, isRTL } = useI18n();
  const common = legal.legal.common;
  const terms = legal.legal.privacy.terms;
  const sections = terms.sections;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F5FF] to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-4xl">
          {/* Back Link */}
          <div className="pt-16 mb-8">
            <Link
              to="/legal"
              className={`inline-flex items-center text-[#5B3FD0] hover:text-[#4A2FB0] transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <svg className={`w-5 h-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {legal.legal.pages.terms.title}
            </Link>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border-t-4 border-[#5B3FD0]">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">{terms.title}</h1>
            <p className="text-[#707070] mb-2">{common.lastUpdated} February 12, 2026</p>
            <p className="text-[#707070] mb-2">{common.effectiveDate} February 12, 2026</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#707070] mt-4">
              <span>{terms.appInfo.appName.label} <strong>{terms.appInfo.appName.value}</strong></span>
              <span>{terms.appInfo.developer.label} <strong>{terms.appInfo.developer.value}</strong></span>
              <span>{terms.appInfo.contact.label} <strong>{terms.appInfo.contact.value}</strong></span>
              <span>{terms.appInfo.website.label} <a href={terms.appInfo.website.value} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">{terms.appInfo.website.value}</a></span>
            </div>
          </div>

          {/* Introduction */}
          <Section title={sections.introduction.title}>
            {sections.introduction.content.map((p, i) => (
              <p key={i} className="text-[#555] leading-relaxed mb-3">{p}</p>
            ))}
          </Section>

          {/* Service Description */}
          <Section title={sections.serviceDescription.title}>
            <p className="text-[#555] mb-4">{sections.serviceDescription.description}</p>
            <div className="space-y-3">
              {sections.serviceDescription.items.map((item, i) => (
                <div key={i} className="bg-[#F8F5FF] rounded-lg p-4">
                  <strong className="text-[#2C2C2C]">{item.label}:</strong>
                  <span className="text-[#555] ml-2">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#707070] mt-4 italic">{sections.serviceDescription.note}</p>
          </Section>

          {/* User Accounts */}
          <Section title={sections.userAccounts.title}>
            {/* Account Creation */}
            <SubSection title={sections.userAccounts.accountCreation.title}>
              <p className="text-[#555] mb-3">{sections.userAccounts.accountCreation.description}</p>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.userAccounts.accountCreation.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </SubSection>

            {/* Account Security */}
            <SubSection title={sections.userAccounts.accountSecurity.title}>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.userAccounts.accountSecurity.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </SubSection>

            {/* Account Termination */}
            <SubSection title={sections.userAccounts.accountTermination.title}>
              <p className="text-[#555] leading-relaxed">{sections.userAccounts.accountTermination.content}</p>
            </SubSection>
          </Section>

          {/* User Conduct */}
          <Section title={sections.userConduct.title}>
            <SubSection title={sections.userConduct.permissibleUse.title}>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.userConduct.permissibleUse.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </SubSection>

            <SubSection title={sections.userConduct.prohibitedActivities.title}>
              <p className="text-[#555] mb-3">{sections.userConduct.prohibitedActivities.description}</p>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.userConduct.prohibitedActivities.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </SubSection>
          </Section>

          {/* Consultation */}
          <Section title={sections.consultation.title}>
            <SubSection title={sections.consultation.serviceOfferings.title}>
              <p className="text-[#555] leading-relaxed">{sections.consultation.serviceOfferings.content}</p>
            </SubSection>
            <SubSection title={sections.consultation.noGuarantee.title}>
              <p className="text-[#555] leading-relaxed">{sections.consultation.noGuarantee.content}</p>
            </SubSection>
            <SubSection title={sections.consultation.studentResponsibilities.title}>
              <p className="text-[#555] leading-relaxed">{sections.consultation.studentResponsibilities.content}</p>
            </SubSection>
          </Section>

          {/* Payment */}
          <Section title={sections.payment.title}>
            <SubSection title={sections.payment.pricingPayment.title}>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.payment.pricingPayment.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </SubSection>
            <SubSection title={sections.payment.refundPolicy.title}>
              <p className="text-[#555] leading-relaxed">{sections.payment.refundPolicy.content}</p>
            </SubSection>
            <SubSection title={sections.payment.paymentReceipts.title}>
              <p className="text-[#555] leading-relaxed">{sections.payment.paymentReceipts.content}</p>
            </SubSection>
          </Section>

          {/* Intellectual Property */}
          <Section title={sections.intellectualProperty.title}>
            <SubSection title={sections.intellectualProperty.platformOwnership.title}>
              <p className="text-[#555] leading-relaxed">{sections.intellectualProperty.platformOwnership.content}</p>
            </SubSection>
            <SubSection title={sections.intellectualProperty.userContent.title}>
              <p className="text-[#555] leading-relaxed">{sections.intellectualProperty.userContent.content}</p>
            </SubSection>
            <SubSection title={sections.intellectualProperty.aiContent.title}>
              <p className="text-[#555] leading-relaxed">{sections.intellectualProperty.aiContent.content}</p>
            </SubSection>
          </Section>

          {/* Privacy */}
          <Section title={sections.privacy.title}>
            <p className="text-[#555] leading-relaxed">
              {sections.privacy.content}{' '}
              <Link to={sections.privacy.privacyLink} className="text-[#5B3FD0] hover:underline font-medium">
                {legal.legal.pages.privacy.title}
              </Link>
            </p>
          </Section>

          {/* Third Party */}
          <Section title={sections.thirdParty.title}>
            <p className="text-[#555] leading-relaxed">{sections.thirdParty.content}</p>
          </Section>

          {/* Disclaimers */}
          <Section title={sections.disclaimers.title}>
            <SubSection title={sections.disclaimers.serviceAvailability.title}>
              <p className="text-[#555] leading-relaxed">{sections.disclaimers.serviceAvailability.content}</p>
            </SubSection>
            <SubSection title={sections.disclaimers.informationAccuracy.title}>
              <p className="text-[#555] leading-relaxed">{sections.disclaimers.informationAccuracy.content}</p>
            </SubSection>
            <SubSection title={sections.disclaimers.limitationOfLiability.title}>
              <p className="text-[#555] mb-3">{sections.disclaimers.limitationOfLiability.description}</p>
              <ul className="space-y-2 list-disc list-inside text-[#555]">
                {sections.disclaimers.limitationOfLiability.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
              <p className="text-sm text-[#707070] mt-3 italic">{sections.disclaimers.limitationOfLiability.note}</p>
            </SubSection>
          </Section>

          {/* Indemnification */}
          <Section title={sections.indemnification.title}>
            <p className="text-[#555] mb-3">{sections.indemnification.description}</p>
            <ul className="space-y-2 list-disc list-inside text-[#555]">
              {sections.indemnification.items.map((item, i) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          </Section>

          {/* Modifications */}
          <Section title={sections.modifications.title}>
            <p className="text-[#555] leading-relaxed">{sections.modifications.content}</p>
          </Section>

          {/* Termination */}
          <Section title={sections.termination.title}>
            <p className="text-[#555] leading-relaxed">{sections.termination.content}</p>
          </Section>

          {/* Governing Law */}
          <Section title={sections.governingLaw.title}>
            <SubSection title={sections.governingLaw.governingLaw.title}>
              <p className="text-[#555] leading-relaxed">{sections.governingLaw.governingLaw.content}</p>
            </SubSection>
            <SubSection title={sections.governingLaw.disputeResolution.title}>
              <p className="text-[#555] leading-relaxed">{sections.governingLaw.disputeResolution.content}</p>
            </SubSection>
          </Section>

          {/* Miscellaneous */}
          <Section title={sections.miscellaneous.title}>
            <SubSection title={sections.miscellaneous.entireAgreement.title}>
              <p className="text-[#555] leading-relaxed">{sections.miscellaneous.entireAgreement.content}</p>
            </SubSection>
            <SubSection title={sections.miscellaneous.severability.title}>
              <p className="text-[#555] leading-relaxed">{sections.miscellaneous.severability.content}</p>
            </SubSection>
            <SubSection title={sections.miscellaneous.waiver.title}>
              <p className="text-[#555] leading-relaxed">{sections.miscellaneous.waiver.content}</p>
            </SubSection>
            <SubSection title={sections.miscellaneous.assignment.title}>
              <p className="text-[#555] leading-relaxed">{sections.miscellaneous.assignment.content}</p>
            </SubSection>
            <SubSection title={sections.miscellaneous.language.title}>
              <p className="text-[#555] leading-relaxed">{sections.miscellaneous.language.content}</p>
            </SubSection>
          </Section>

          {/* Contact */}
          <Section title={sections.contact.title}>
            <p className="text-[#555] mb-4">{sections.contact.description}</p>
            <div className="bg-[#F8F5FF] rounded-lg p-6 space-y-2">
              <p className="text-[#555]">{common.company} <strong>ByteLogs LTD</strong></p>
              <p className="text-[#555]">{common.email} <a href="mailto:support@edunasser.com" className="text-[#5B3FD0] hover:underline">support@edunasser.com</a></p>
              <p className="text-[#555]">{common.website} <a href="https://edunasser.com" className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">https://edunasser.com</a></p>
            </div>
          </Section>

          {/* Acknowledgment */}
          <Section title={sections.acknowledgment.title}>
            <p className="text-[#555] leading-relaxed">{sections.acknowledgment.content}</p>
          </Section>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-sm text-[#707070]">
              © {new Date().getFullYear()} {legal.legal.mainPage.footer.rights} <strong>{legal.legal.mainPage.company}</strong>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
      <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4 pb-2 border-b-2 border-[#5B3FD0]">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-lg font-semibold text-[#2C2C2C] mb-3">{title}</h3>
      {children}
    </div>
  );
}
